/**
 * The one persisted `currentForgeWorkspaceId` (CMMD-Center/forge#3572),
 * replacing the five independent guessing sites the issue names. Read it with
 * `useCurrentForgeWorkspaceState()` (React) or `getCurrentForgeWorkspaceId()`
 * (outside a component, e.g. a fetch-time resolver).
 *
 * Two ways to set it, because they carry different side effects:
 *
 * - `seedCurrentForgeWorkspaceId` -- called once, at the moment the account's
 *   workspace is first confirmed (a single-membership auto-resolve, or the
 *   user's first explicit pick in the "ask" step). When the store starts a
 *   session with nothing persisted, there is nothing scoped to tear down.
 *   When it starts from a stale persisted id that this confirm corrects to a
 *   different one, that stale id may already have scoped a fetch, so this
 *   drops the cached catalog rows and refetches on the same singleton, same
 *   as `switchCurrentForgeWorkspaceId` below (CMMD-Center/forge#3572 review).
 * - `switchCurrentForgeWorkspaceId` -- called from the shell switcher, always
 *   (never `seedCurrentForgeWorkspaceId`, even from an unset starting value:
 *   the switcher is a separate actor from whatever else is reading the
 *   store, most notably a mounted first-run hook, and cannot know whether
 *   its target differs from what that hook already assumed). Drops the
 *   locally cached catalog rows and forces a fresh fetch on the SAME shared
 *   singleton (never a new one -- see below), clears the stale command-center
 *   workspace filter, requests an authoritative CMMD work-intake refetch so
 *   the sidebar/Homebase/command-palette/breadcrumb snapshot moves to the new
 *   org instead of waiting for the next scheduled sync (CMMD-Center/forge#3647),
 *   then bumps `switchGeneration` so a first-run gate can tell "the workspace
 *   changed under me" apart from "I just confirmed my own resolution."
 *
 *   That fresh fetch does NOT re-scope what the catalog shows: CMMD's
 *   `/api/forge/context/cloud-projects` fans out across every membership
 *   unconditionally, by design, so the refetched rows are the same
 *   cross-workspace set as before the switch. Only the stale-cache problem
 *   is fixed here; scoping the catalog's own display and the inventory
 *   resource refresh to `currentForgeWorkspaceId` is wired in
 *   CMMD-Center/forge#3576.
 *
 *   Refetching on the same singleton, rather than tearing it down and
 *   nulling it, matters for a different reason: `subscribeCmmdForgeRuntimeList`
 *   is a stable module-level function, so `useSyncExternalStore` never
 *   re-subscribes just because the singleton pointer changed underneath it.
 *   Nulling it would leave every already-mounted consumer's listener
 *   registered on a dead instance that nothing will ever notify again.
 */
import { useSyncExternalStore } from "react";

import { clearCachedRows } from "../cmmd/cmmdForgeRuntimeListCache";
import { refreshCmmdForgeRuntimeList } from "../cmmd/cmmdForgeRuntimeListResource";
import { cmmdWorkIntakeResource } from "../cmmd/workIntakeResource";
import { writeStoredWorkspaceFilter } from "../components/command-center/workspaceFilterStorage";
import { isCmmdEmbedMode } from "../embed/cmmdEmbedMode";
import { readStoredValue, removeStoredValue, writeStoredValue } from "../lib/storage";

const STORAGE_KEY = "forge:current-workspace-id";

export interface CurrentForgeWorkspaceState {
  readonly workspaceId: number | null;
  /** Increments only on an explicit switch, never on a same-value confirm. */
  readonly switchGeneration: number;
}

function readStoredWorkspaceId(): number | null {
  const raw = readStoredValue(STORAGE_KEY);
  if (raw === null || !/^\d+$/.test(raw)) return null;
  const parsed = Number.parseInt(raw, 10);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

let state: CurrentForgeWorkspaceState = {
  workspaceId: readStoredWorkspaceId(),
  switchGeneration: 0,
};
const listeners = new Set<() => void>();

function emit(next: CurrentForgeWorkspaceState): void {
  state = next;
  for (const listener of listeners) listener();
}

function getCurrentForgeWorkspaceState(): CurrentForgeWorkspaceState {
  return state;
}

export function getCurrentForgeWorkspaceId(): number | null {
  return state.workspaceId;
}

function subscribeCurrentForgeWorkspaceState(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function seedCurrentForgeWorkspaceId(organizationId: number): void {
  if (state.workspaceId === organizationId) return;
  const correctingAStaleId = state.workspaceId !== null;
  writeStoredValue(STORAGE_KEY, String(organizationId));
  if (correctingAStaleId) {
    clearCachedRows();
    refreshCmmdForgeRuntimeList();
  }
  emit({ ...state, workspaceId: organizationId });
}

export function switchCurrentForgeWorkspaceId(organizationId: number): void {
  // Embed sessions stay on the org CMMD handed off; no second switcher path.
  if (isCmmdEmbedMode()) return;
  if (state.workspaceId === organizationId) return;
  writeStoredValue(STORAGE_KEY, String(organizationId));
  clearCachedRows();
  refreshCmmdForgeRuntimeList();
  writeStoredWorkspaceFilter(null);
  emit({ workspaceId: organizationId, switchGeneration: state.switchGeneration + 1 });
  // Same fresh-fetch requirement as the runtime list above, but for the CMMD
  // work-intake snapshot (sidebar, Homebase, command palette, breadcrumbs):
  // without this the snapshot keeps the previous workspace's data until the
  // next scheduled sync (forge#3647). Authoritative because the switch is a
  // user-visible action that must bypass the serving cache, not wait for it
  // to expire.
  cmmdWorkIntakeResource.requestRefetch({ authoritative: true });
}

/** Test-only: clears the module-level singleton back to "nothing persisted". */
export function __resetCurrentForgeWorkspaceStoreForTests(): void {
  removeStoredValue(STORAGE_KEY);
  state = { workspaceId: null, switchGeneration: 0 };
  listeners.clear();
}

export function useCurrentForgeWorkspaceState(): CurrentForgeWorkspaceState {
  return useSyncExternalStore(
    subscribeCurrentForgeWorkspaceState,
    getCurrentForgeWorkspaceState,
    getCurrentForgeWorkspaceState,
  );
}
